import React, { useEffect, useMemo, useState } from 'react';
import { collection } from 'firebase/firestore';
import { db } from '../../../../firebase-client';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import EditButton from '../../../ui/EditButton';

function dutyRoles(event = {}) {
  return (Array.isArray(event.roles) ? event.roles : []).filter((role) => {
    if (role?.dutyRoster === true) return true;
    return (Array.isArray(role?.slots) ? role.slots : []).some((slot) => slot?.dutyRoster === true || slot?.assignedByRoster === true);
  });
}

function toDate(value) {
  if (value?.toDate) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function eventStart(event = {}) {
  const explicit = toDate(event.start);
  if (explicit) return explicit;
  if (event.date) {
    const parsed = new Date(`${event.date}T${event.time || '00:00'}`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function formatDutyDate(millis) {
  if (!Number.isFinite(millis) || millis === Number.MAX_SAFE_INTEGER) return '';
  return new Date(millis).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

export default function DutyRosterCard({
  teamId,
  modules = {},
  userId = '',
  isAdmin = false,
  onManage,
}) {
  const enabled = Boolean(modules?.sailing?.dutyRoster?.enabled);
  const [events, setEvents] = useState([]);
  const [swaps, setSwaps] = useState([]);

  useEffect(() => {
    if (!enabled || !teamId) {
      setEvents([]);
      return undefined;
    }
    return safeOnSnapshot(
      collection(db, 'teams', teamId, 'events'),
      (snapshot) => {
        setEvents(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      undefined,
      { label: 'sailing-duty-roster-events', teamId }
    );
  }, [enabled, teamId]);

  useEffect(() => {
    if (!enabled || !teamId) {
      setSwaps([]);
      return undefined;
    }
    return safeOnSnapshot(
      collection(db, 'teams', teamId, 'dutySwapRequests'),
      (snapshot) => {
        setSwaps(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      undefined,
      { label: 'sailing-duty-roster-swaps', teamId }
    );
  }, [enabled, teamId]);

  const summary = useMemo(() => {
    const now = Date.now();
    return events.reduce((acc, event) => {
      const start = eventStart(event);
      const future = !start || start.getTime() >= now;
      dutyRoles(event).forEach((role) => {
        (Array.isArray(role.slots) ? role.slots : []).forEach((slot) => {
          if (future) acc.upcoming += 1;
          if (!slot?.assigneeUserId || slot?.responseStatus === 'declined') acc.warnings += 1;
          if (slot?.assigneeUserId === userId && future) {
            acc.mine += 1;
            if (String(slot?.responseStatus || '').toLowerCase() === 'pending') acc.myPending += 1;
            if (!acc.nextDuty || (start?.getTime?.() || Number.MAX_SAFE_INTEGER) < acc.nextDuty.startMillis) {
              acc.nextDuty = {
                eventName: event.name || 'Event',
                roleName: role.name || 'Duty',
                startMillis: start?.getTime?.() || Number.MAX_SAFE_INTEGER,
              };
            }
          }
          if (slot?.responseStatus === 'pending') acc.pending += 1;
        });
      });
      return acc;
    }, { upcoming: 0, warnings: 0, mine: 0, myPending: 0, pending: 0, nextDuty: null });
  }, [events, userId]);

  const openSwapCount = swaps.filter((swap) => {
    if (!['open', 'pendingAdmin'].includes(String(swap.status || 'open'))) return false;
    if (isAdmin || swap.requestedBy === userId || swap.acceptingMemberId === userId) return true;
    return Array.isArray(swap.eligibleMemberIds) && swap.eligibleMemberIds.includes(userId);
  }).length;

  if (!enabled) return null;

  const primaryActionTab = summary.myPending > 0 ? 'mine' : (isAdmin ? 'grid' : 'mine');
  const primaryActionLabel = summary.myPending > 0 ? 'Respond' : (isAdmin ? 'Manage' : 'Open');
  const primaryPanel = summary.myPending > 0
    ? {
        tone: 'attention',
        eyebrow: 'Response needed',
        title: `${summary.myPending} duty ${summary.myPending === 1 ? 'needs' : 'need'} your reply`,
        meta: summary.nextDuty ? `${summary.nextDuty.eventName} • ${summary.nextDuty.roleName}` : 'Open My Duties to review.',
        action: 'Review duties',
      }
    : summary.nextDuty
      ? {
          tone: 'normal',
          eyebrow: 'Next duty',
          title: summary.nextDuty.roleName,
          meta: `${summary.nextDuty.eventName}${formatDutyDate(summary.nextDuty.startMillis) ? ` • ${formatDutyDate(summary.nextDuty.startMillis)}` : ''}`,
          action: 'My duties',
        }
      : {
          tone: 'quiet',
          eyebrow: isAdmin ? 'Roster overview' : 'No duties assigned',
          title: isAdmin ? `${summary.warnings} roster ${summary.warnings === 1 ? 'issue' : 'issues'}` : 'Nothing waiting for you',
          meta: isAdmin ? `${summary.upcoming} upcoming duty slots` : 'New assignments will appear here.',
          action: isAdmin ? 'Open roster' : 'Open',
        };

  const stats = isAdmin
    ? [
        ['Roster issues', summary.warnings, summary.warnings > 0 ? 'danger' : 'ok'],
        ['Pending replies', summary.pending, summary.pending > 0 ? 'warn' : 'ok'],
        ['Open swaps', openSwapCount, openSwapCount > 0 ? 'danger' : 'ok'],
      ]
    : [
        ['My duties', summary.mine, 'ok'],
        ['Responses', summary.myPending, summary.myPending > 0 ? 'danger' : 'ok'],
        ['Swaps', openSwapCount, openSwapCount > 0 ? 'warn' : 'ok'],
      ];

  return (
    <div className="team-card duty-roster-card">
      <div className="card-header-with-icon">
        <span className="card-title">Duty Roster</span>
        <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          <button type="button" className="btn btn--secondary btn--xs" onClick={() => onManage?.({ initialTab: primaryActionTab })}>
            {primaryActionLabel}
          </button>
          {isAdmin && <EditButton section="dutyRoster" onEdit={() => onManage?.({ initialTab: 'grid' })} />}
        </div>
      </div>
      <div className="card-body duty-roster-card__body">
        <button
          type="button"
          className={`duty-roster-card__primary duty-roster-card__primary--${primaryPanel.tone}`}
          onClick={() => onManage?.({ initialTab: summary.myPending > 0 || summary.nextDuty ? 'mine' : primaryActionTab })}
        >
          <div>
            <span className="duty-roster-card__eyebrow">{primaryPanel.eyebrow}</span>
            <strong>{primaryPanel.title}</strong>
            <span>{primaryPanel.meta}</span>
          </div>
          <span className="duty-roster-card__action">{primaryPanel.action}</span>
        </button>

        <div className="duty-roster-card__stats" aria-label="Duty roster summary">
          {stats.map(([label, value, tone]) => (
            <button
              key={label}
              type="button"
              className={`duty-roster-card__stat duty-roster-card__stat--${tone}`}
              onClick={() => onManage?.({ initialTab: label.includes('Swap') || label === 'Swaps' ? 'swaps' : (isAdmin && label !== 'Pending replies' ? 'grid' : 'mine') })}
            >
              <span>{label}</span>
              <strong>{value}</strong>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
